import type { InquiryPayload } from '@/types'

export type InquiryErrors = Partial<Record<keyof InquiryPayload, string>>

export const EMPTY_INQUIRY: InquiryPayload = {
    fullName: '',
    email: '',
    phone: '',
    departure: '',
    destination: '',
    travelDate: '',
    passengers: 2,
    notes: '',
    company: ''
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const PHONE_PATTERN = /^[+\d][\d\s().-]{5,}$/

const text = (value: unknown, max: number): string => {
    if (typeof value !== 'string') return ''
    return value.trim().slice(0, max)
}

// Formats a date as YYYY-MM-DD in the visitor's own time zone
export const toDateString = (date: Date): string => {
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${date.getFullYear()}-${month}-${day}`
}

export const validateInquiry = (
    input: Partial<Record<keyof InquiryPayload, unknown>>,
    earliestDate: string
): { values: InquiryPayload; errors: InquiryErrors } => {
    const passengers = Number(input.passengers)

    const values: InquiryPayload = {
        fullName: text(input.fullName, 120),
        email: text(input.email, 160).toLowerCase(),
        phone: text(input.phone, 40),
        departure: text(input.departure, 120),
        destination: text(input.destination, 120),
        travelDate: text(input.travelDate, 10),
        passengers: Number.isFinite(passengers) ? Math.round(passengers) : 0,
        notes: text(input.notes, 1500),
        company: text(input.company, 120)
    }

    const errors: InquiryErrors = {}

    if (values.fullName.length < 2) errors.fullName = 'Please tell us your name.'
    if (!EMAIL_PATTERN.test(values.email)) errors.email = 'Please enter a valid email address.'
    if (values.phone && !PHONE_PATTERN.test(values.phone)) errors.phone = 'Please check this number, or leave it empty.'
    if (values.departure.length < 2) errors.departure = 'Where would you like to fly from?'
    if (values.destination.length < 2) errors.destination = 'Where would you like to go?'

    if (!DATE_PATTERN.test(values.travelDate)) {
        errors.travelDate = 'Please choose a travel date.'
    } else if (earliestDate && values.travelDate < earliestDate) {
        errors.travelDate = 'Please choose a date from today onwards.'
    }

    if (values.passengers < 1 || values.passengers > 19) errors.passengers = 'Choose between 1 and 19 passengers.'

    return { values, errors }
}
